import React, { FunctionComponent, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Alert,
    Paper,
    Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CloudArrowUpIcon } from '@heroicons/react/24/outline';
import { StarjackTable } from '@starjack/shared-components';
import { OrderTicketCollection } from '../model/orderTicket.types';
import { ValidatedImportRow } from '../lib/xlsxImportProcessor';
import { formatDateToString } from '../lib/orderTicketUtils';

type UsePaginationReturn<T> = {
    currentItems: T[];
    setNextPage: (page: number) => void;
    page: number;
    setItemsPerPage: (itemsPerPage: number) => void;
    itemsPerPage: number;
};

type XlsxImportDialogProps = {
    open: boolean;
    onClose: () => void;
    onImport: (importedData: ValidatedImportRow[]) => void;
    availableTickets: OrderTicketCollection[];
    useParseFileData: () => (file: File) => Promise<unknown[]>;
    useValidateImportData: () => (
        data: unknown[],
        availableTickets: OrderTicketCollection[]
    ) => ValidatedImportRow[];
    usePagination: <T>(params: {
        items: T[];
        itemsPerPage?: number;
    }) => UsePaginationReturn<T>;
    columnMapping: Record<string, string>;
};

export const XlsxImportDialog: FunctionComponent<XlsxImportDialogProps> = ({
    open,
    onClose,
    onImport,
    availableTickets,
    useParseFileData,
    useValidateImportData,
    usePagination,
    columnMapping,
}) => {
    const { t } = useTranslation();
    const [importData, setImportData] = useState<ValidatedImportRow[]>([]);
    const [error, setError] = useState<string>('');
    const [isDragOver, setIsDragOver] = useState(false);
    const { currentItems, setNextPage, page, setItemsPerPage, itemsPerPage } =
        usePagination({ items: importData, itemsPerPage: 10 });
    const validateData = useValidateImportData();
    const parseFileData = useParseFileData();

    const handleFileUpload = async (file: File) => {
        try {
            const parsedData = await parseFileData(file);
            const validatedData = validateData(parsedData, availableTickets);
            setImportData(validatedData);
            setError('');
        } catch (err) {
            setError((err as Error).message);
            setImportData([]);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = () => {
        setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);

        const files = Array.from(e.dataTransfer.files);
        const dataFile = files.find(
            (file) => file.name.endsWith('.xlsx') || file.name.endsWith('.xls')
        );
        if (dataFile) {
            handleFileUpload(dataFile);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileUpload(file);
        }
    };

    const handleImport = () => {
        onImport(importData);
        onClose();
        setImportData([]);
        setError('');
    };

    const handleClose = () => {
        onClose();
        setImportData([]);
        setError('');
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='lg' fullWidth>
            <DialogTitle>{t('ticketOrder.xlsxImport.title')}</DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ mb: 2 }}
                    >
                        {t('ticketOrder.xlsxImport.description')}
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            mb: 2,
                        }}
                    >
                        {Object.keys(columnMapping)
                            .filter((key) =>
                                Object.values(columnMapping).includes(
                                    columnMapping[
                                        key as keyof typeof columnMapping
                                    ]
                                )
                            )
                            .map((columnName) => (
                                <Chip
                                    key={columnName}
                                    label={columnName}
                                    size='small'
                                    variant='outlined'
                                />
                            ))}
                    </Box>
                    <Box>
                        <Typography
                            variant='body2'
                            color='text.secondary'
                            sx={{}}
                            gutterBottom
                        >
                            {t('ticketOrder.xlsxImport.columnsRestrictedTo')}
                        </Typography>
                        <Box
                            sx={{
                                ml: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <Chip
                                    sx={{
                                        mr: 1,
                                    }}
                                    label='Title'
                                    size='small'
                                    variant='outlined'
                                />
                                <Typography
                                    variant='subtitle2'
                                    component='span'
                                >
                                    {t('ticketOrder.xlsxImport.titleValues')}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Paper
                    sx={{
                        p: 3,
                        mb: 3,
                        border: '2px dashed',
                        borderColor: isDragOver ? 'primary.main' : 'grey.300',
                        backgroundColor: isDragOver
                            ? 'action.hover'
                            : 'transparent',
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                    }}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() =>
                        document.getElementById('xlsx-file-input')?.click()
                    }
                >
                    <CloudArrowUpIcon
                        style={{ width: 48, height: 48, margin: '0 auto 16px' }}
                    />

                    <Typography variant='h6' gutterBottom>
                        {t('ticketOrder.xlsxImport.dropZone')}
                    </Typography>

                    <Typography variant='body2' color='text.secondary'>
                        {t(
                            'ticketOrder.xlsxImport.fileTypes',
                            'Supported file types: .xlsx'
                        )}
                    </Typography>

                    <input
                        id='xlsx-file-input'
                        type='file'
                        accept='.xlsx,.xls'
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                    />
                </Paper>

                {error && (
                    <Alert severity='error' sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {importData.length > 0 && (
                    <Box>
                        <Typography variant='h6' gutterBottom>
                            {t('ticketOrder.xlsxImport.preview', 'Preview')} (
                            {importData.length}{' '}
                            {t('ticketOrder.xlsxImport.rows', 'rows')})
                        </Typography>
                        <StarjackTable<ValidatedImportRow>
                            count={importData.length}
                            items={currentItems}
                            columns={[
                                {
                                    label: 'Ticket Name',
                                    itemKey: 'ticketName',
                                },
                                {
                                    label: 'Product ID',
                                    itemKey: 'personName',
                                },
                                {
                                    label: 'Title',
                                    itemKey: 'title',
                                },
                                {
                                    label: 'First Name',
                                    itemKey: 'firstname',
                                },
                                {
                                    label: 'Last Name',
                                    itemKey: 'lastname',
                                },
                                {
                                    label: 'Birthdate',
                                    itemTransform: (item) => {
                                        const date =
                                            typeof item.birthdate === 'string'
                                                ? new Date(item.birthdate)
                                                : item.birthdate;
                                        return formatDateToString(
                                            date instanceof Date ? date : null
                                        );
                                    },
                                },
                            ]}
                            currentPage={page}
                            rowsPerPage={itemsPerPage}
                            onPageChange={setNextPage}
                            onRowsPerPageChange={setItemsPerPage}
                            rowsSelectable={false}
                            loading={false}
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose}>
                    {t('ticketOrder.xlsxImport.cancel', 'Cancel')}
                </Button>
                <Button
                    onClick={handleImport}
                    variant='contained'
                    disabled={importData.length === 0}
                >
                    {t('ticketOrder.xlsxImport.import', 'Import')} (
                    {importData.length})
                </Button>
            </DialogActions>
        </Dialog>
    );
};
